import React, { useState, useEffect } from 'react';
import { UpdateSplitRequest } from '../types';
import { PrimaryButton, SecondaryButton } from './buttons';

interface UpdateSplitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updateData: UpdateSplitRequest) => void;
  isLoading: boolean;
  split: {
    title: string;
    collaborators: Array<{
      name: string;
      role: 'composer' | 'author' | 'both';
      share: number;
      email?: string;
      ipiNumber?: string;
    }>;
  };
}

const UpdateSplitModal: React.FC<UpdateSplitModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  split
}) => {
  const [formData, setFormData] = useState<UpdateSplitRequest>({
    songTitle: split.title,
    collaborators: split.collaborators.map(c => ({
      name: c.name,
      role: c.role,
      share: c.share,
      email: c.email || '',
      ipiNumber: c.ipiNumber || ''
    }))
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        songTitle: split.title,
        collaborators: split.collaborators.map(c => ({
          name: c.name,
          role: c.role,
          share: c.share,
          email: c.email || '',
          ipiNumber: c.ipiNumber || ''
        }))
      });
    }
  }, [isOpen, split]);

  if (!isOpen) return null;

  const totalShares = formData.collaborators.reduce((sum, c) => sum + c.share, 0);
  const isValidShares = Math.abs(totalShares - 100) <= 0.01;
  const hasAllEmails = formData.collaborators.every(c => c.email.trim() !== '');
  const isValid = isValidShares && hasAllEmails && formData.songTitle.trim() !== '';

  const updateCollaborator = (index: number, field: keyof UpdateSplitRequest['collaborators'][0], value: string | number) => {
    const updatedCollaborators = [...formData.collaborators];
    updatedCollaborators[index] = {
      ...updatedCollaborators[index],
      [field]: value
    };
    setFormData({
      ...formData,
      collaborators: updatedCollaborators
    });
  };

  const addCollaborator = () => {
    setFormData({
      ...formData,
      collaborators: [
        ...formData.collaborators,
        {
          name: '',
          role: 'composer',
          share: 0,
          email: '',
          ipiNumber: ''
        }
      ]
    });
  };

  const removeCollaborator = (index: number) => {
    if (formData.collaborators.length > 1) {
      const updatedCollaborators = formData.collaborators.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        collaborators: updatedCollaborators
      });
    }
  };

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#27272A] rounded-2xl p-6 w-full max-w-4xl mx-4 border border-[#3F3F46] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2.5 2.5 0 113.536 3.536L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Update Split & Resend
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-[#6B7280] hover:text-white transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* Song Title */}
          <div>
            <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
              Song Title *
            </label>
            <input
              type="text"
              value={formData.songTitle}
              onChange={(e) => setFormData({ ...formData, songTitle: e.target.value })}
              disabled={isLoading}
              className="w-full px-3 py-2 bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent disabled:opacity-50"
              placeholder="Enter song title"
            />
          </div>

          {/* Collaborators */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-[#9CA3AF]">
                Collaborators
              </label>
              <button
                onClick={addCollaborator}
                disabled={isLoading}
                className="text-sm px-3 py-1 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0D9488] transition-colors disabled:opacity-50"
              >
                + Add Collaborator
              </button>
            </div>

            <div className="space-y-4">
              {formData.collaborators.map((collaborator, index) => (
                <div key={index} className="grid grid-cols-1 lg:grid-cols-5 gap-3 p-4 bg-[#1F1F22] rounded-lg border border-[#3F3F46]">
                  <div>
                    <label className="block text-xs font-medium text-[#9CA3AF] mb-1">Name *</label>
                    <input
                      type="text"
                      value={collaborator.name}
                      onChange={(e) => updateCollaborator(index, 'name', e.target.value)}
                      disabled={isLoading}
                      className="w-full px-2 py-1 text-sm bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:ring-[#14B8A6] disabled:opacity-50"
                      placeholder="Full name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#9CA3AF] mb-1">Role</label>
                    <select
                      value={collaborator.role}
                      onChange={(e) => updateCollaborator(index, 'role', e.target.value as 'composer' | 'author' | 'both')}
                      disabled={isLoading}
                      className="w-full px-2 py-1 text-sm bg-[#18181B] border border-[#3F3F46] rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-[#14B8A6] disabled:opacity-50"
                    >
                      <option value="composer">Composer</option>
                      <option value="author">Author</option>
                      <option value="both">Both</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#9CA3AF] mb-1">Share % *</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={collaborator.share}
                      onChange={(e) => updateCollaborator(index, 'share', parseFloat(e.target.value) || 0)}
                      disabled={isLoading}
                      className="w-full px-2 py-1 text-sm bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:ring-[#14B8A6] disabled:opacity-50"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#9CA3AF] mb-1">Email *</label>
                    <input
                      type="email"
                      value={collaborator.email}
                      onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                      disabled={isLoading}
                      className="w-full px-2 py-1 text-sm bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:ring-[#14B8A6] disabled:opacity-50"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div className="flex items-end space-x-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-[#9CA3AF] mb-1">IPI Number</label>
                      <input
                        type="text"
                        value={collaborator.ipiNumber}
                        onChange={(e) => updateCollaborator(index, 'ipiNumber', e.target.value)}
                        disabled={isLoading}
                        className="w-full px-2 py-1 text-sm bg-[#18181B] border border-[#3F3F46] rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:ring-[#14B8A6] disabled:opacity-50"
                        placeholder="Optional"
                      />
                    </div>
                    {formData.collaborators.length > 1 && (
                      <button
                        onClick={() => removeCollaborator(index)}
                        disabled={isLoading}
                        className="p-1 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                        title="Remove collaborator"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Validation Summary */}
            <div className="mt-4 p-3 bg-[#1F1F22] rounded-lg border border-[#3F3F46]">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#9CA3AF]">Total Shares:</span>
                <span className={`font-medium ${isValidShares ? 'text-green-400' : 'text-red-400'}`}>
                  {totalShares.toFixed(2)}%
                  {isValidShares ? ' ✓' : ' ❌ Must equal 100%'}
                </span>
              </div>
              {!hasAllEmails && (
                <p className="text-red-400 text-sm mt-2">❌ All collaborators must have email addresses</p>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <p className="text-yellow-400 text-sm font-medium mb-2">⚠️ This action will:</p>
            <ul className="text-yellow-400 text-sm space-y-1 list-disc list-inside">
              <li>Update the split with new details</li>
              <li>Reset ALL collaborator confirmations</li>
              <li>Send new invite emails to all collaborators</li>
              <li>Invalidate old confirmation links</li>
            </ul>
          </div>
        </div>

        <div className="flex space-x-3 mt-6">
          <SecondaryButton
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            disabled={isLoading || !isValid}
            loading={isLoading}
            className="flex-1"
          >
            Update & Resend to All
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default UpdateSplitModal;
